/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { FormattedMessage as OfficialFormattedMessage } from 'react-intl' // eslint-disable-line no-restricted-imports

import { useConfig } from '@modules/cui/ConfigContext'
import Feature from '@modules/utils/feature'

type Props = React.ComponentProps<typeof OfficialFormattedMessage> & {
  /**
   * By default, translated strings are assumed to be benign. If sensitive data
   * is interpolated into this string, specify `true`.
   */
  sensitive?: true
  /**
   * If you want to output raw HTML without a wrapping element (e.g. for use
   * with marked-up text in EuiText), specify `true`.
   */
  rawOutput?: true
}

/**
 * A thin wrapper around react-intl's `FormattedMessage` that explicitly masks
 * or unmasks the content from third-party analytics tools. Use instead of the
 * stock `FormattedMessage` to ensure that our i18ned strings are:
 *
 *   - visible in our analytics tooling by default
 *   - obscured from our analytics tooling when marked as sensitive
 */
export const FormattedMessage: React.FunctionComponent<Props> = ({
  sensitive,
  rawOutput,
  ...props
}) => {
  const isFullStoryActivated = Boolean(useConfig(Feature.userconsoleRunFullStory))

  // Don't clutter the DOM if there's no analytics tooling to worry about, or if
  // directly instructed to output raw HTML.
  if (!isFullStoryActivated || rawOutput) {
    return <OfficialFormattedMessage {...props} />
  }

  // Otherwise, supply the masking classes to the FormattedMessage output (in
  // child renderer form as there's no first-class className prop on
  // FormattedMessage).
  const Component = props.tagName ?? 'span'
  const className = sensitive ? 'fs-mask' : 'fs-unmask'

  return (
    <OfficialFormattedMessage {...props}>
      {(text) => <Component className={className}>{text}</Component>}
    </OfficialFormattedMessage>
  )
}

FormattedMessage.displayName = 'FormattedMessageWrapper'

// ts-unused-exports:disable-next-line
export const Mask: React.FunctionComponent<
  React.PropsWithChildren<{ tagName?: React.ElementType<any> }>
> = ({ children, tagName = 'span' }) => {
  const isFullStoryActivated = useConfig(Feature.userconsoleRunFullStory)

  // Don't clutter the DOM if there's no analytics tooling to worry about.
  if (!isFullStoryActivated) {
    return <Fragment>{children}</Fragment>
  }

  const Component = tagName

  return <Component className='fs-mask'>{children}</Component>
}

export const Unmask: React.FunctionComponent<
  React.PropsWithChildren<{ tagName?: React.ElementType<any> }>
> = ({ children, tagName = 'span' }) => {
  const isFullStoryActivated = useConfig(Feature.userconsoleRunFullStory)

  // Don't clutter the DOM if there's no analytics tooling to worry about.
  if (!isFullStoryActivated) {
    return <Fragment>{children}</Fragment>
  }

  const Component = tagName

  return <Component className='fs-unmask'>{children}</Component>
}
